import React, { useState, useRef } from 'react';
import { useEffect } from 'react';

/*
 * Small "?" info bubble. Works on desktop (hover) AND mobile (tap to toggle,
 * tap anywhere else to dismiss) — most of our users are on phones.
 *
 * Positioning is automatic: when opened it measures the available space and
 * aligns the bubble left/center/right (and above/below) so it never clips
 * off a small screen. The `align` prop is kept for API compatibility but
 * only used as a hint when everything fits.
 */
const BUBBLE_HALF = 130; // half of bubble width (240px) + margin

const InfoTip = ({ text, align = 'center', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ h: align, v: 'top' });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const openTip = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      const centerX = r.left + r.width / 2;
      const spaceLeft = centerX;
      const spaceRight = window.innerWidth - centerX;
      let h = 'center';
      if (spaceLeft < BUBBLE_HALF && spaceRight >= BUBBLE_HALF) h = 'left';
      else if (spaceRight < BUBBLE_HALF && spaceLeft >= BUBBLE_HALF) h = 'right';
      else if (spaceLeft >= BUBBLE_HALF && spaceRight >= BUBBLE_HALF) h = 'center';
      else h = align; // extremely narrow — fall back to the hint
      const v = r.top < 130 ? 'bottom' : 'top'; // flip below if near viewport top
      setPos({ h, v });
    }
    setOpen(true);
  };

  const bubbleH =
    pos.h === 'right' ? 'right-0' : pos.h === 'left' ? 'left-0' : 'left-1/2 -translate-x-1/2';
  const arrowH =
    pos.h === 'right' ? 'right-2' : pos.h === 'left' ? 'left-2' : 'left-1/2 -translate-x-1/2';
  const bubbleV = pos.v === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';
  const arrowV =
    pos.v === 'top'
      ? 'top-full border-t-slate-800'
      : 'bottom-full border-b-slate-800';

  return (
    <span ref={ref} className={`relative inline-flex align-middle ${className}`}>
      <button
        type="button"
        aria-label="More info"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (open) setOpen(false);
          else openTip();
        }}
        onMouseEnter={openTip}
        onMouseLeave={() => setOpen(false)}
        style={{ touchAction: 'manipulation' }}
        className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-sky-100 hover:text-sky-600 text-[10px] font-bold leading-none transition-colors cursor-help"
      >
        ?
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute ${bubbleV} ${bubbleH} w-60 max-w-[75vw] z-50 rounded-xl bg-slate-800 text-white text-xs font-normal normal-case tracking-normal leading-relaxed px-3 py-2 shadow-xl pointer-events-none text-left`}
        >
          {text}
          <span className={`absolute ${arrowV} ${arrowH} border-4 border-transparent`}></span>
        </span>
      )}
    </span>
  );
};

export default InfoTip;
